import aa from 'search-insights'
import { getSearchConfig } from './client.js'

const USER_TOKEN_KEY = 'algolia_anonymous_user_token'
const TRACKED_KINDS = new Set(['product', 'resource', 'project', 'page'])

let insightsInitialized = false
let userToken = ''

const createUserToken = () => {
    if (window.crypto?.randomUUID) {
        return `anonymous-${window.crypto.randomUUID()}`
    }

    const bytes = new Uint8Array(16)
    if (window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(bytes)
    } else {
        bytes.forEach((_, index) => {
            bytes[index] = Math.floor(Math.random() * 256)
        })
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const id = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')

    return `anonymous-${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`
}

export const getOrCreateAnonymousUserToken = () => {
    if (userToken) return userToken

    try {
        userToken = window.sessionStorage.getItem(USER_TOKEN_KEY) || ''
        if (!userToken) {
            userToken = createUserToken()
            window.sessionStorage.setItem(USER_TOKEN_KEY, userToken)
        }
    } catch {
        // Keep an in-memory token for this page when sessionStorage is unavailable.
        userToken = createUserToken()
    }

    return userToken
}

export const initSearchInsights = () => {
    if (insightsInitialized) return true

    const config = getSearchConfig()
    if (config.provider !== 'algolia' || !config.appId || !config.searchKey || !config.indexName) return false

    try {
        const token = getOrCreateAnonymousUserToken()
        aa('init', {
            appId: config.appId,
            apiKey: config.searchKey,
            useCookie: false,
            userToken: token,
        })
        aa('setUserToken', token)
        insightsInitialized = true
        return true
    } catch {
        return false
    }
}

export const buildSearchResultTracking = (kind) => {
    if (!TRACKED_KINDS.has(kind)) return ''

    return JSON.stringify({
        event: 'Interaction',
        event_type: 'Search Result Click',
        result_kind: kind,
    })
}

const pushTrackingBeforeNavigation = (tracking, onComplete) => {
    if (!tracking || !Array.isArray(window.dataLayer)) return false

    let completed = false
    let timeout
    const complete = () => {
        if (completed) return
        completed = true
        window.clearTimeout(timeout)
        onComplete()
    }

    timeout = window.setTimeout(complete, 500)
    window.dataLayer.push({
        ...tracking,
        eventCallback: complete,
    })

    return true
}

export const trackSearchResultInteraction = (kind, onComplete) => {
    const tracking = buildSearchResultTracking(kind)
    return pushTrackingBeforeNavigation(tracking ? JSON.parse(tracking) : null, onComplete)
}

export const trackSearchResultInteractionNow = (kind) => {
    const tracking = buildSearchResultTracking(kind)
    if (!tracking || !Array.isArray(window.dataLayer)) return false

    window.dataLayer.push(JSON.parse(tracking))
    return true
}

export const trackNavigationSearch = onComplete => pushTrackingBeforeNavigation({
    event: 'Interaction',
    event_type: 'Nav - Search',
}, onComplete)

export const trackResultClick = ({ indexName, queryID, objectID, position }) => {
    const resolvedPosition = Number(position)
    if (
        !indexName ||
        !queryID ||
        objectID === undefined ||
        objectID === null ||
        !Number.isInteger(resolvedPosition) ||
        resolvedPosition < 1 ||
        !initSearchInsights()
    ) return

    try {
        aa('clickedObjectIDsAfterSearch', {
            eventName: 'Search Result Clicked',
            index: indexName,
            queryID,
            objectIDs: [String(objectID)],
            positions: [resolvedPosition],
        })
    } catch {
        // Analytics must never prevent the selected result from opening.
    }
}
